// useUnreadNotifications.js — Hook qui poll /api/messages/unread-count et déclenche
// une notification navigateur quand le nombre augmente.
// Demande la permission au premier rendu si pas encore décidée (lazy, après le 1er fetch).
import { useEffect, useRef, useState } from 'react';
import { api } from './api';

const POLL_INTERVAL_MS = 30 * 1000; // 30 secondes
const STORAGE_KEY = 'assoperf_notifications_pref';

export default function useUnreadNotifications({ enabled = true, label = 'messages' } = {}) {
  const [unread, setUnread] = useState(0);
  const [perm, setPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const lastValueRef = useRef(0);
  const intervalRef = useRef(null);

  // Demande la permission (l'utilisateur peut le faire via askPermission)
  const askPermission = async () => {
    if (typeof Notification === 'undefined') return 'unsupported';
    if (Notification.permission === 'granted' || Notification.permission === 'denied') {
      setPerm(Notification.permission);
      return Notification.permission;
    }
    const result = await Notification.requestPermission();
    setPerm(result);
    localStorage.setItem(STORAGE_KEY, result);
    return result;
  };

  useEffect(() => {
    if (!enabled) return;

    const fetchOnce = async () => {
      try {
        const { data } = await api.get('/messages/unread-count');
        const n = data?.unread || 0;
        if (n > lastValueRef.current && lastValueRef.current >= 0) {
          // Notification navigateur si permission accordée
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            const diff = n - lastValueRef.current;
            try {
              new Notification('Nouveau message', {
                body: `${diff} nouveau${diff > 1 ? 'x' : ''} ${label} non lu${diff > 1 ? 's' : ''}`,
                icon: '/favicon.ico',
                tag: 'assoperf-messages', // évite empilement
                silent: false,
              });
            } catch {}
          }
        }
        lastValueRef.current = n;
        setUnread(n);
      } catch { /* fail silently */ }
    };

    // Fetch initial puis poll
    fetchOnce();
    intervalRef.current = setInterval(fetchOnce, POLL_INTERVAL_MS);

    // Refresh quand la fenêtre redevient visible
    const onVisibility = () => { if (document.visibilityState === 'visible') fetchOnce(); };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, label]);

  return { unread, perm, askPermission };
}
